import axios from "axios";
import { useCallback, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { DCDescriptionTemplate, DCDescriptionTemplateWidget } from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const widgetFields = "id template_id widget_type row_index col_index config created_at updated_at";
const templateFields = `id brand_id name is_default created_at updated_at widgets { ${widgetFields} }`;

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, {
        headers: { "X-Wallmates-Auth": authToken }
    });
    if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
    return data.data;
};

export const useDescriptionTemplates = () => {
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();
    const [templates, setTemplates] = useState<DCDescriptionTemplate[]>([]);
    const [loading, setLoading] = useState(false);

    const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
        try {
            setLoading(true);
            return await fn();
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Request failed" });
            return null;
        } finally {
            setLoading(false);
        }
    }, [loginFunctions.logout, setSnackbar]);

    // #region listTemplates
    const listTemplates = useCallback((brandId: number) => run(async () => {
        const q = `query($brand_id:ID!){ dcDescriptionTemplates(brand_id:$brand_id){ ${templateFields} } }`;
        const data = await graphqlRequest(authToken, q, { brand_id: brandId });
        const next = (data?.dcDescriptionTemplates || []) as DCDescriptionTemplate[];
        setTemplates(next);
        return next;
    }), [authToken, run]);

    // #region getTemplate
    const getTemplate = useCallback((id: number) => run(async () => {
        const q = `query($id:ID!){ dcDescriptionTemplate(id:$id){ ${templateFields} } }`;
        const data = await graphqlRequest(authToken, q, { id });
        return (data?.dcDescriptionTemplate || null) as DCDescriptionTemplate | null;
    }), [authToken, run]);

    // #region getLinkedCount
    const getLinkedCount = useCallback((templateId: number) => run(async () => {
        const q = `query($template_id:ID!){ dcDescriptionTemplateLinkedCount(template_id:$template_id) }`;
        const data = await graphqlRequest(authToken, q, { template_id: templateId });
        return (data?.dcDescriptionTemplateLinkedCount ?? 0) as number;
    }), [authToken, run]);

    // #region createTemplate
    const createTemplate = useCallback((input: { brand_id: number; name: string; widgets?: Partial<DCDescriptionTemplateWidget>[] }) => run(async () => {
        const q = `mutation($input:DCDescriptionTemplateInput!){ createDCDescriptionTemplate(input:$input){ ${templateFields} } }`;
        const mapped = {
            ...input,
            widgets: input.widgets?.map(w => ({
                widget_type: w.widget_type,
                row_index: w.row_index,
                col_index: w.col_index,
                config: typeof w.config === "string" ? w.config : JSON.stringify(w.config ?? {}),
            })),
        };
        const data = await graphqlRequest(authToken, q, { input: mapped });
        const created = data?.createDCDescriptionTemplate as DCDescriptionTemplate;
        setTemplates(prev => [...prev, created]);
        return created;
    }), [authToken, run]);

    // #region updateTemplate
    const updateTemplate = useCallback((id: number, input: { name?: string; is_default?: boolean }) => run(async () => {
        const q = `mutation($id:ID!,$input:DCDescriptionTemplateUpdateInput!){ updateDCDescriptionTemplate(id:$id,input:$input){ ${templateFields} } }`;
        const data = await graphqlRequest(authToken, q, { id, input });
        const updated = data?.updateDCDescriptionTemplate as DCDescriptionTemplate;
        setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
        return updated;
    }), [authToken, run]);

    // #region deleteTemplate
    const deleteTemplate = useCallback((id: number) => run(async () => {
        const q = `mutation($id:ID!){ deleteDCDescriptionTemplate(id:$id) }`;
        const data = await graphqlRequest(authToken, q, { id });
        const success = Boolean(data?.deleteDCDescriptionTemplate);
        if (success) setTemplates(prev => prev.filter(t => t.id !== id));
        return success;
    }), [authToken, run]);

    // #region setDefault
    const setDefault = useCallback((brandId: number, templateId: number | null) => run(async () => {
        const q = `mutation($brand_id:ID!,$template_id:ID){ setDefaultDCDescriptionTemplate(brand_id:$brand_id,template_id:$template_id) }`;
        const data = await graphqlRequest(authToken, q, { brand_id: brandId, template_id: templateId });
        if (data?.setDefaultDCDescriptionTemplate) {
            setTemplates(prev => prev.map(t => ({ ...t, is_default: t.id === templateId })));
        }
        return Boolean(data?.setDefaultDCDescriptionTemplate);
    }), [authToken, run]);

    // #region replaceWidgets
    const replaceWidgets = useCallback((templateId: number, widgets: Partial<DCDescriptionTemplateWidget>[]) => run(async () => {
        const q = `mutation($template_id:ID!,$widgets:[DCDescriptionTemplateWidgetInput!]!){
            replaceDCDescriptionTemplateWidgets(template_id:$template_id,widgets:$widgets){
                ${widgetFields}
            }
        }`;
        const mappedWidgets = widgets.map(w => ({
            widget_type: w.widget_type,
            row_index: w.row_index,
            col_index: w.col_index,
            config: typeof w.config === "string" ? w.config : JSON.stringify(w.config ?? {}),
        }));
        const data = await graphqlRequest(authToken, q, { template_id: templateId, widgets: mappedWidgets });
        return (data?.replaceDCDescriptionTemplateWidgets || []) as DCDescriptionTemplateWidget[];
    }), [authToken, run]);

    return {
        templates,
        loading,
        listTemplates,
        getTemplate,
        getLinkedCount,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        setDefault,
        replaceWidgets,
    };
};
